import { useCallback, useEffect, useRef } from "react";
import Axios from "axios";
import { environment } from "../../environment";
import { errorCatcher, useSnackbar } from "../../hooks";
import { useLogin } from "../login";
import { IPluginSubscriptionRate, IPluginSubscriptionRateView } from "./types";

// #region usePluginSubscriptionRate
/**
 * SUPERADMIN-only Shopify plugin monthly rate (`/api/v2/admin/plugin-subscription-rate`).
 * Every save appends a new rate version; brands already subscribed keep the rate
 * locked at signup. Errors surface the API `message` in a snackbar.
 */
export const usePluginSubscriptionRate = () => {
    const [, setSnackbar] = useSnackbar();
    const [, authToken, loginFunctions] = useLogin();
    const loginFunctionsRef = useRef(loginFunctions);
    useEffect(() => { loginFunctionsRef.current = loginFunctions; }, [loginFunctions]);

    // #region getPluginSubscriptionRate
    const getPluginSubscriptionRate = useCallback(async (): Promise<IPluginSubscriptionRateView | null> => {
        try {
            const result = await Axios.get<IPluginSubscriptionRateView>(environment.pluginSubscriptionRateUrl, {
                headers: { 'X-Wallmates-Auth': authToken }
            });
            return result.data;
        } catch (err: any) {
            errorCatcher(err, loginFunctionsRef.current.logout);
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: err?.response?.data?.message ?? 'There was an error loading the plugin subscription rate.'
            });
            return null;
        }
    }, [authToken]); // eslint-disable-line

    // #region setPluginSubscriptionRate
    const setPluginSubscriptionRate = useCallback(async (amount: number): Promise<IPluginSubscriptionRate | null> => {
        try {
            const result = await Axios.post<IPluginSubscriptionRate>(environment.pluginSubscriptionRateUrl, { monthly_rate: amount }, {
                headers: { 'X-Wallmates-Auth': authToken },
                // Creates a Stripe Price before inserting the row.
                timeout: 30_000
            });
            setSnackbar({ show: true, snackbarLevel: 'info', text: 'Plugin subscription rate updated.' });
            return result.data;
        } catch (err: any) {
            errorCatcher(err, loginFunctionsRef.current.logout);
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: err?.response?.data?.message ?? 'There was an error updating the plugin subscription rate.'
            });
            return null;
        }
    }, [authToken]); // eslint-disable-line

    return { getPluginSubscriptionRate, setPluginSubscriptionRate };
};
